import { convertCurrency } from "../converter/currency"

const DEFAULT_MAXIMUM_FRACTION_DIGITS = 2

/**
 * Format an amount expressed in minor units (cents) into a currency string.
 *
 * @example
 * formatCurrency(2000) // "20 €"
 * formatCurrency(1050) // "10,50 €"
 * formatCurrency(1051) // "10,51 €"
 */
export function formatCurrency(
    amount: number,
    options: Intl.NumberFormatOptions = {},
    locales = ["fr-FR"]
) {
    const majorAmount = convertCurrency(amount)
    const maximumFractionDigits =
        typeof options.maximumFractionDigits === "number"
            ? options.maximumFractionDigits
            : DEFAULT_MAXIMUM_FRACTION_DIGITS

    // Never truncate cents: an amount with a fractional part is padded so that
    // `10,50 €` and `10,51 €` render consistently. Round amounts keep no
    // decimals, and a narrower `maximumFractionDigits` still takes precedence.
    const defaultMinimumFractionDigits = Number.isInteger(majorAmount)
        ? 0
        : Math.min(DEFAULT_MAXIMUM_FRACTION_DIGITS, maximumFractionDigits)

    const minimumFractionDigits = options.minimumFractionDigits ?? defaultMinimumFractionDigits

    const formatter = new Intl.NumberFormat(locales, {
        ...options,
        style: "currency",
        currency: options.currency || "EUR",
        maximumFractionDigits: Math.max(minimumFractionDigits, maximumFractionDigits),
        minimumFractionDigits,
    })

    return formatter.format(convertCurrency(amount))
}
